import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowLeftIcon, EyeIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuotePreview } from '../../components/admin/QuotePreview';
import { useAdminData } from '../../contexts/AdminDataContext';
import { defaultValidUntil, emptyLine, today } from '../../utils/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const NEW_CUSTOMER = '__ny__';
export function QuoteNew() {
  const {
    customers,
    enquiries,
    createQuote,
    settings,
    authLoading,
    lastWriteError
  } = useAdminData();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? NEW_CUSTOMER);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [enquiryId, setEnquiryId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([{
    ...emptyLine(),
    unitPrice: Number(settings.hourlyRate) || 0,
    unit: 'timer'
  }]);
  const [quoteDate, setQuoteDate] = useState(today());
  const [validUntil, setValidUntil] = useState(defaultValidUntil(today()));
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('Tilbuddet er gældende i 30 dage. Prisen er inkl. moms. Uforudsete opgaver aftales altid på forhånd.');
  const [saving, setSaving] = useState<QuoteStatus | null>(null);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const creatingCustomer = customerId === NEW_CUSTOMER || customers.length === 0;
  const selected = customers.find(entry => entry.id === customerId);
  async function save(status: QuoteStatus) {
    if (saving) return;
    // Der gemmes aldrig, mens Firebase stadig gendanner sessionen.
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    if (!title.trim()) {
      setError('Giv tilbuddet en titel, før du gemmer.');
      return;
    }
    if (creatingCustomer && !newCustomer.name.trim()) {
      setError('Skriv kundens navn, før du gemmer.');
      return;
    }
    setSaving(status);
    setError('');
    const created = await createQuote({
      customerId: creatingCustomer ? '' : customerId,
      newCustomer: creatingCustomer ? newCustomer : undefined,
      enquiryId: enquiryId || undefined,
      title: title.trim(),
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes,
      status
    });
    setSaving(null);
    if (!created) {
      // Viser den faktiske Firestore-årsag (fx permission-denied) i stedet for en generisk besked.
      setError(lastWriteError() || 'Tilbuddet kunne ikke gemmes. Tjek din forbindelse, og prøv igen.');
      return;
    }
    navigate(`/admin/tilbud/${created.id}`);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1454" data-mp="2ln3v49ewsmdu">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-1455" data-mp="2ql5gx23pyzt0">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1456" data-mp="2zmd2qkp1u867" />
        Tilbud
      </Link>
      <h1 className="mt-4 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1457" data-mp="2zkir5wzo3ses">
        Opret tilbud
      </h1>

      <form className="mt-7 max-w-4xl space-y-5" onSubmit={event => {
      event.preventDefault();
      void save('Kladde');
    }} data-id="element-1458" data-mp="39d7sw05f1rwx">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-1459" data-mp="3idcxy3n50e7x">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1460" data-mp="3ib08z6iyp3ld">Kunde</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1461" data-mp="3n9l790enld76">
            <SelectField id="nt-kunde" label="Vælg kunde" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[...customers.map(customer => ({
            value: customer.id,
            label: customer.name
          })), {
            value: NEW_CUSTOMER,
            label: '+ Opret ny kunde'
          }]} data-id="element-1462" data-mp="39nw80n8yikl9" />
            <SelectField id="nt-henvendelse" label="Tilknyt henvendelse" hint="Valgfrit — knytter tilbuddet til en henvendelse fra hjemmesiden." value={enquiryId} onChange={event => setEnquiryId(event.target.value)} options={[{
            value: '',
            label: 'Ingen'
          }, ...enquiries.map(enquiry => ({
            value: enquiry.id,
            label: `${enquiry.name} — ${enquiry.description.slice(0, 40)}`
          }))]} data-id="element-1463" data-mp="2qqv0auo0q6dm" />
          </div>

          {creatingCustomer && <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1464" data-mp="1sz6hhq0l1say">
              <TextField id="nt-navn" label="Navn" required value={newCustomer.name} onChange={event => setNewCustomer({
            ...newCustomer,
            name: event.target.value
          })} data-id="element-1465" data-mp="1xwptej9t5szq" />
              <TextField id="nt-tlf" label="Telefonnummer" type="tel" value={newCustomer.phone} onChange={event => setNewCustomer({
            ...newCustomer,
            phone: event.target.value
          })} data-id="element-1466" data-mp="2ckawid6ons7n" />
              <TextField id="nt-mail" label="E-mail" type="email" value={newCustomer.email} onChange={event => setNewCustomer({
            ...newCustomer,
            email: event.target.value
          })} data-id="element-1467" data-mp="22t6qw42wdjph" />
              <TextField id="nt-adresse" label="Adresse" value={newCustomer.address} onChange={event => setNewCustomer({
            ...newCustomer,
            address: event.target.value
          })} data-id="element-1468" data-mp="30bq34i5hsvop" />
            </div>}
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1469" data-mp="306mzd6d85l9q">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1470" data-mp="2v83bh0f2lzeq">Opgave</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1471" data-mp="2r7rtxwi08f7h">
            <TextField id="nt-titel" label="Tilbudstitel" required placeholder="Fx montering af hegn" className="sm:col-span-2" value={title} onChange={event => setTitle(event.target.value)} data-id="element-1472" data-mp="2mgt86he8yowu" />
            <TextField id="nt-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
            setQuoteDate(event.target.value);
            setValidUntil(defaultValidUntil(event.target.value));
          }} data-id="element-1473" data-mp="27jix395inl1n" />
            <TextField id="nt-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => setValidUntil(event.target.value)} data-id="element-1474" data-mp="n0qanzyxygij" />
          </div>
          <TextArea id="nt-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => setDescription(event.target.value)} data-id="element-1475" data-mp="wyw9u9td58jp" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1476" data-mp="99bhx1f8f0ya">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1477" data-mp="e137b8mnj0mu">Tilbudslinjer</h2>
          <div className="mt-5" data-id="element-1478" data-mp="3crli8atb1pkr">
            <QuoteLinesEditor lines={lines} onChange={setLines} data-id="element-1479" data-mp="3cqado8s1cdsl" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1480" data-mp="3hobv6p9tc51y">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1481" data-mp="2b3lzinw7xluh">Noter</h2>
          <TextArea id="nt-kundenoter" label="Bemærkninger til kunden" hint="Vises i tilbuddet." className="mt-5" rows={3} value={customerNotes} onChange={event => setCustomerNotes(event.target.value)} data-id="element-1482" data-mp="2649bhztm28qb" />
          <TextArea id="nt-internenoter" label="Interne noter" hint="Vises kun i admin." className="mt-5" rows={3} value={internalNotes} onChange={event => setInternalNotes(event.target.value)} data-id="element-1483" data-mp="2u0naqqgd8i6v" />
        </section>

        {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1484" data-mp="2ysyigfhcl89z">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1485" data-mp="3csg0mc051nad" />
            {error}
          </p>}

        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-1486" data-mp="37r0eshq4zcqx">
          <Button type="submit" size="lg" disabled={saving !== null} data-id="element-1487" data-mp="3rfy8tkcwt01j">
            {saving === 'Kladde' ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1488" data-mp="69auvi08ia5" />
                Gemmer …
              </> : 'Gem kladde'}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => setPreviewing(true)} disabled={saving !== null} data-id="element-1489" data-mp="3mhmyl8ipcnis">
            <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1490" data-mp="3dgyk6cpecl5d" />
            Forhåndsvis tilbud
          </Button>
          <Button type="button" variant="dark" size="lg" onClick={() => void save('Sendt')} disabled={saving !== null} data-id="element-1491" data-mp="2yo4rfpl468ir">
            {saving === 'Sendt' ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1492" data-mp="2tpthleutjy0j" />
                Gemmer …
              </> : 'Marker som sendt'}
          </Button>
        </div>
      </form>

      {previewing && <QuotePreview quoteNumber="Nyt tilbud" title={title} description={description} lines={lines} quoteDate={quoteDate} validUntil={validUntil} customerNotes={customerNotes} customer={creatingCustomer ? newCustomer : {
      name: selected?.name ?? '',
      address: selected?.address ?? '',
      email: selected?.email ?? '',
      phone: selected?.phone ?? ''
    }} onClose={() => setPreviewing(false)} data-id="element-1493" data-mp="2vjgkmac73teo" />}
    </div>;
}